import React from 'react';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  detail?: string;
}

export default function ConfirmModal({
  title,
  message,
  confirmLabel = 'Delete',
  onConfirm,
  onCancel,
  danger = true,
  loading = false,
  icon,
  detail,
}: ConfirmModalProps) {
  return (
    <div className="cm-overlay">
      <div className="cm-modal" onClick={e => e.stopPropagation()}>
        {danger && (
          <div className="cm-icon-wrap">
            {icon ?? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            )}
          </div>
        )}
        <h3 className="cm-title">{title}</h3>
        <p className="cm-message">{message}</p>
        {detail && <p className="cm-detail">{detail}</p>}
        <div className="cm-actions">
          <button className="btn btn-secondary btn-sm cm-btn-cancel" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
          <button
            className={`btn btn-sm ${danger ? 'cm-btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <span className="cm-spinner" />
            ) : confirmLabel}
          </button>
        </div>
      </div>
      <style>{`
        .cm-overlay {
          position: fixed; inset: 0;
          background: rgba(15,23,42,0.55);
          backdrop-filter: blur(2px);
          z-index: 2000;
          display: flex; align-items: center; justify-content: center; padding: 16px;
          animation: cm-fade-in 0.15s ease;
        }
        @keyframes cm-fade-in { from { opacity: 0 } to { opacity: 1 } }
        .cm-modal {
          background: white;
          border-radius: 16px;
          box-shadow: 0 24px 80px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.08);
          width: 100%; max-width: 420px;
          padding: 28px 28px 24px;
          animation: cm-slide-in 0.18s cubic-bezier(.22,.68,0,1.2);
        }
        @keyframes cm-slide-in { from { opacity: 0; transform: translateY(12px) scale(0.97) } to { opacity: 1; transform: translateY(0) scale(1) } }
        .cm-icon-wrap {
          width: 52px; height: 52px;
          background: #fef2f2;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: var(--red, #ef4444);
          margin-bottom: 16px;
        }
        .cm-title { font-size: 17px; font-weight: 700; color: var(--navy); margin: 0 0 8px; }
        .cm-message { font-size: 14px; color: var(--gray-600, #4b5563); margin: 0 0 6px; line-height: 1.55; }
        .cm-detail { font-size: 12px; color: var(--gray-400); margin: 0 0 24px; }
        .cm-message + .cm-actions { margin-top: 24px; }
        .cm-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 24px; }
        .cm-btn-cancel { color: var(--gray-600); }
        .cm-btn-danger { background: var(--red, #ef4444); color: white; border: none; min-width: 80px; display: flex; align-items: center; justify-content: center; gap: 6px; }
        .cm-btn-danger:hover:not(:disabled) { background: #dc2626; }
        .cm-btn-danger:disabled, .cm-btn-cancel:disabled { opacity: 0.6; cursor: not-allowed; }
        .cm-spinner {
          width: 14px; height: 14px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: white;
          border-radius: 50%;
          animation: cm-spin 0.6s linear infinite;
          display: inline-block;
        }
        @keyframes cm-spin { to { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
}
